import React from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, inr } from '@hrms/shared';
import { AutoGrid } from '../../payroll/components/common';
import { api } from '../../../lib/api';
import { Alert, Card, DescList, ErrorState, Loading, PageHeader, Screen, StatCard, Text, useTheme } from '../../../ui';

export function BillingScreen() {
  const { colors } = useTheme();
  const q = useQuery({ queryKey: ['billing'], queryFn: () => api.admin.billing() });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <Screen>
        <PageHeader title="Billing" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </Screen>
    );
  const b = q.data;
  const usage = b.seats ? Math.round((b.active_employees / b.seats) * 100) : 0;
  const estimate = b.active_employees * b.price_per_employee;
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="billing">
      <PageHeader title="Billing" subtitle="Subscription, seats and upcoming invoice." />
      <AutoGrid min={200}>
        <StatCard label="Plan" value={b.plan.charAt(0).toUpperCase() + b.plan.slice(1)} icon="card" />
        <StatCard label="Active employees" value={b.active_employees} hint={`${b.seats} seats`} icon="users" tone={usage > 90 ? 'warning' : 'success'} />
        <StatCard label="Price per employee" value={inr(b.price_per_employee)} hint="per month" icon="wallet" tone="info" />
        <StatCard label="Next invoice" value={fmtDate(b.next_invoice_date)} hint={`Est. ${inr(estimate)}`} icon="receipt" tone="neutral" />
      </AutoGrid>
      <Card title="Seat usage">
        <View style={{ height: 10, backgroundColor: colors.surface2, borderRadius: 6, overflow: 'hidden', borderWidth: 1, borderColor: colors.border }} accessibilityRole="progressbar" accessibilityValue={{ min: 0, max: 100, now: usage }}>
          <View style={{ width: `${Math.min(100, usage)}%`, height: '100%', backgroundColor: usage > 90 ? colors.warning : colors.primary }} />
        </View>
        <Text variant="small" style={{ marginTop: 8 }}>{`${b.active_employees} of ${b.seats} seats used (${usage}%)`}</Text>
      </Card>
      <Card title="Next invoice">
        <DescList
          items={[
            ['Invoice date', fmtDate(b.next_invoice_date)],
            ['Billable employees', String(b.active_employees)],
            ['Rate', `${inr(b.price_per_employee)} / employee / month`],
            ['Estimated amount', inr(estimate)],
          ]}
        />
      </Card>
      {usage > 90 && <Alert tone="warning">You're close to your seat limit. Contact support to add seats.</Alert>}
    </Screen>
  );
}
